import { inject } from 'inversify';
import { Userpilot } from 'userpilot';
import {
  createRouter,
  createWebHistory,
  MENU,
  NavigationGuard,
  RouteRecord,
  RouteRecordNormalized,
  RouteRecordRaw,
} from 'vue-router';
import multiguard from 'vue-router-multiguard';

import { IRouteLike, IRouter } from './types';

import { CLIENT_TYPE, APP } from '@/constants';
import { UserController } from '@/controllers/UserController';
import { service } from '@/decorators/service';
import { t, te } from '@/locales';
import { Connection } from '@/services/Connection';
import { ProxyClass } from '@/utils/ProxyClass';
import { pathJoin } from '@/utils/pathJoin';

@service(Router)
export class Router extends ProxyClass(createRouter({
  history: createWebHistory(),
  routes: [],
}), 'VueRouter') implements IRouter {
  // Services.
  @inject(Connection) connection: Connection;
  @inject(UserController) userController: UserController;

  // Methods.
  constructor() {
    super();

    this.afterEach(async (to) => {
      const route = to.matched.slice().reverse().pop();
      if (route) {
        await this.setTitle(route);
      }
    });

    this.afterEach(() => {
      Userpilot.reload();
    });

    // Ensure click handler is bound to the proxy.
    this.interceptLinkClick = this.interceptLinkClick.bind(this.$proxy);

    // This class intentionally creates a proxy for vue-router, return it.
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    // eslint-disable-next-line no-constructor-return
    return this.$proxy;
  }

  addGuards(...guards: NavigationGuard[]) {
    this.beforeEach(multiguard(guards.map((guard) => guard.bind(this))));

    return this;
  }

  addRoutes(...routes: RouteRecordRaw[]) {
    routes.forEach((route) => this.addRoute(route));

    return this;
  }

  getMenuRoutes(menu: MENU, checkAccess: boolean = true): RouteRecordNormalized[] {
    const menuRoutes = this.getRoutes().filter((route) => route.meta?.menus?.includes(menu));

    if (!checkAccess) {
      return menuRoutes;
    }

    const checkRouteAccess = (route: RouteRecord | RouteRecordRaw) => {
      const access = this.routeHasAccess(route);

      // Check child route access.
      if (access && route.children?.length) {
        route.children = route.children.filter(checkRouteAccess);
      }

      return access;
    };

    return menuRoutes.filter(checkRouteAccess);
  }

  async getRouteDescription(route?: IRouteLike): Promise<string> {
    let description = '';
    if (route?.meta?.description) {
      if (typeof route.meta.description === 'function') {
        description = await route.meta.description();
      }
      else {
        description = `${route.meta.description}`;
      }
    }
    else if (route?.name && route.meta?.translateDescription !== false) {
      const key = `routes.${String(route.name)}.description`;
      if (te(key)) {
        description = t(key);
      }
    }

    return description;
  }

  async getRouteTitle(route?: IRouteLike): Promise<string> {
    let title = '';
    if (route?.meta?.title) {
      if (typeof route.meta.title === 'function') {
        title = await route.meta.title();
      }
      else {
        title = `${route.meta.title}`;
      }
    }
    else if (route?.name && route.meta?.translateTitle !== false) {
      const key = `routes.${String(route.name)}.title`;
      if (te(key)) {
        title = t(key);
      }
    }

    return title;
  }

  hasPath(path: string): boolean {
    const normalizedPath = pathJoin(path);
    const pathMatch = (route: RouteRecordRaw) => pathJoin(route.path) === normalizedPath;
    const routeMatch = (route: RouteRecordRaw) => pathMatch(route) || route.children?.some(routeMatch);
    return this.getRoutes().some(routeMatch);
  }

  async interceptLinkClick(event: MouseEvent) {
    // Ensure we use the link, in case the click has been received by a sub-element.
    let { target } = <MouseEvent & { target: HTMLAnchorElement }> event;

    while (target && target.tagName !== 'A') target = <HTMLAnchorElement> target.parentNode;

    // Immediately return if target is not an anchor.
    if (!target || !target.href) {
      return;
    }

    const url = new URL(target.href, window.location.origin);
    const hasPath = this.hasPath && this.hasPath(url.pathname);

    if (
      // Ignore blank target.
      target.getAttribute('target')?.match(/\b_blank\b/i) ||
      // Ignore modifier keys.
      event.metaKey || event.altKey || event.ctrlKey || event.shiftKey ||
      // Ignore defaultPrevented and right clicks.
      event.defaultPrevented || event.button !== 0 ||
      // Ignore different origin (hostname).
      url.origin !== window.location.origin ||
      // Ignore links that have no matching router path.
      !hasPath
    ) {
      return;
    }

    // Router has a valid path, route to it (it may handle a redirect).
    event.preventDefault();
    await this.push(url.href.substring(url.origin.length));
  }

  routeHasAccess(route?: IRouteLike): boolean {
    const access: boolean[] = [];

    // CAD type permission.
    if (route?.meta?.cadState !== undefined) {
      access.push(route.meta.cadState === this.connection.cadState);
    }

    // Role based permission.
    if (route?.meta?.role !== undefined) {
      access.push(this.userController.activeUser.roles.min(route?.meta?.role));
    }

    return access.length
      ? access.every(Boolean)
      : true;
  }

  async setTitle(route: IRouteLike): Promise<void> {
    const title = await this.getRouteTitle(route);
    const appTitle = this.connection.clientType === CLIENT_TYPE.DISPATCH ? APP.TITLE : APP.PORTAL_TITLE;

    document.title = [title, appTitle].filter(Boolean).join(' | ');
  }
}
